import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const PonysContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 40vh;
  background: url('/assets/ponys/all.jpeg') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 6vw, 4rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PonyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PonyCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.card};
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.hover};
  }
`;

const PonyImage = styled.div`
  aspect-ratio: 3/4;
  background: ${props => props.image ? `url(${props.image}) center/cover` : props.theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: white;
`;

const PonyContent = styled.div`
  padding: 2rem;
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 0.5rem;
  }
`;

const Ponys = () => {
  const ponys = [
    {
      id: 'ashley',
      name: 'Ashley',
      image: '/assets/ponys/ashley.jpeg'
    },
    {
      id: 'lucky',
      name: 'Lucky',
      image: '/assets/ponys/lucky.jpeg'
    },
    {
      id: 'tweety',
      name: 'Tweety',
      image: '/assets/ponys/tweety.jpeg'
    },
    {
      id: 'kiki',
      name: 'Kiki',
      image: '/assets/ponys/kiki.jpeg'
    },
    {
      id: 'rusty',
      name: 'Rusty',
      image: '/assets/ponys/rusty.jpeg'
    },
    {
      id: 'rapunzel',
      name: 'Rapunzel',
      image: '/assets/ponys/rapunzel.jpeg'
    },
    {
      id: 'ronja',
      name: 'Ronja',
      image: '/assets/ponys/ronja.jpeg'
    }
  ];

  return (
    <PonysContainer>
      <Helmet>
        <title>Unsere Ponys - Der Ponystall | Lernpartner mit Herz</title>
        <meta name="description" content="Lernen Sie unsere wundervollen Ponys kennen - Ashley, Lucky, Tweety, Kiki, Rusty, Rapunzel und Ronja. Jedes Pony hat seinen besonderen Charakter und seine Stärken." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Unsere Ponys
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <div className="text-center">
            <h2 className="section-title">Unsere vierbeinigen Lehrmeister</h2>
            <p className="section-subtitle">
              Jedes unserer Ponys hat seinen einzigartigen Charakter und besondere Fähigkeiten. 
              Sie sind nicht nur Reittiere, sondern wahre Lehrmeister und therapeutische Partner.
            </p>
          </div>

          <PonyGrid>
            {ponys.map((pony, index) => (
              <PonyCard
                key={pony.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <PonyImage image={pony.image}>
                  {pony.emoji && pony.emoji}
                </PonyImage>
                <PonyContent>
                  <h3>{pony.name}</h3>
                </PonyContent>
              </PonyCard>
            ))}
          </PonyGrid>
        </Container>
      </ContentSection>
    </PonysContainer>
  );
};

export default Ponys;